import { all, call, fork, put, select, takeEvery } from "redux-saga/effects";
import FileSaver from "file-saver";
import {
  fetchSummaryData,
  setSummaryDimensions,
  setSummaryKpi,
  setTableLoading,
  STATS_CHANGE_SUMMARY_DIMENSIONS,
  STATS_CHANGE_SUMMARY_KPI,
  STATS_EXPORT_DATA,
  STATS_EXPORT_GAIN_DATA,
  STATS_EXPORT_RAW,
  STATS_MAIL_COMMANDE_REASSORT,
  STATS_MAIL_SAMPLE_PARODONTAX,
  STATS_MAIL_NB_VISITS_PER_DAY,
  STATS_MAIL_NB_VISITS_PER_DAY_LUCCA,
  STATS_MAIL_ADVIL_GAIN,
  STATS_MAIL_VISIT_RELEVE_PHARMA
} from "../actions/stats";
import { statsApiUpsellProvider } from "../../../lib/api/providers";
import {
  getCurrentFiltersUnSplitted,
  getSummaryDimensions,
  getSummaryKpiId,
  getSummaryKpiSelected,
  getTableLoading
} from "../selectors";
import StatUtils from "../../../utils/stats";

export const getStatsState = state => state.stats;
export const getState = state => state;

/**
 * Get stats data
 * @param kpi
 * @param currentFilters
 * @return {Promise<T | never>}
 */
export const getExportFile = (kpi, currentFilters) =>
  statsApiUpsellProvider
    .post(`/export/${kpi}`, currentFilters, { responseType: "arraybuffer" })
    .then((response = {}) => {
      return response.data;
    })
    .catch(err => {
      throw err;
    });

/**
 * Export stats data (raw)
 * @param kpi
 * @param currentFilters
 * @return {Promise<T | never>}
 */
export const getExportRawFile = (kpi, currentFilters) =>
  statsApiUpsellProvider
    .post(`/export/raw/${kpi}`, currentFilters, { responseType: "arraybuffer" })
    .then((response = {}) => {
      return response.data;
    })
    .catch(err => {
      throw err;
    });

/**
 * Get stats gain data
 * @param kpi
 * @param currentFilters
 * @return {Promise<T | never>}
 */
const getExportGainFile = (kpi, currentFilters) =>
  statsApiUpsellProvider
    .post(`/exportgain/${kpi}`, currentFilters, { responseType: "arraybuffer" })
    .then((response = {}) => {
      return response.data;
    })
    .catch(err => {
      throw err;
    });

/**
 * Export and download data
 * @return {Generator<SelectEffect|*, void, ?>}
 */
function* exportData() {
  const isTableLoading = yield select(getTableLoading);
  if (isTableLoading) {
    return;
  }

  yield put(setTableLoading(true));
  const stats = yield select(getStatsState);
  const summaryKpi = yield select(getSummaryKpiSelected);
  const kpiSelected = stats.kpiSelected;
  const currentFilters = yield select(getCurrentFiltersUnSplitted, null, true);
  const formattedFilters = StatUtils.formatFilters(currentFilters);
  const summaryDimensions = yield select(getSummaryDimensions);
  const summaryKpiId = yield select(getSummaryKpiId);
  const body = {
    filters: formattedFilters,
    period: stats.period,
    period1: stats.periodToCompare,
    summaryKpiId,
    dimensions: summaryDimensions
  };
  try {
    const data = yield call(getExportFile, kpiSelected, body);
    FileSaver.saveAs(
      new Blob([data], { type: "application/vnd.ms-excel" }),
      `${summaryKpi.long_label || "Données"}.xlsx`
    );
    yield put(setTableLoading(false));
  } catch (e) {
    yield put(setTableLoading(false));
  }
}

/**
 * Export and download data
 * @return {Generator<SelectEffect|*, void, ?>}
 */
function* exportRawData() {
  const isTableLoading = yield select(getTableLoading);
  if (isTableLoading) {
    return;
  }

  yield put(setTableLoading(true));
  const stats = yield select(getStatsState);
  const summaryKpi = yield select(getSummaryKpiSelected);
  const kpiSelected = stats.kpiSelected;
  const currentFilters = yield select(getCurrentFiltersUnSplitted, null, true);
  const formattedFilters = StatUtils.formatFilters(currentFilters);
  const summaryDimensions = yield select(getSummaryDimensions);
  const summaryKpiId = yield select(getSummaryKpiId);
  const body = {
    filters: formattedFilters,
    period: stats.period,
    period1: stats.periodToCompare,
    summaryKpiId,
    dimensions: summaryDimensions
  };
  try {
    const data = yield call(getExportRawFile, kpiSelected, body);
    FileSaver.saveAs(
      new Blob([data], { type: "application/vnd.ms-excel" }),
      `${summaryKpi.long_label || "Données"}(Données brutes).xlsx`
    );
    yield put(setTableLoading(false));
  } catch (e) {
    yield put(setTableLoading(false));
  }
}

function* mailCommandeReassort(action) {
  const isTableLoading = yield select(getTableLoading);
  const globalState = yield select(getState);
  if (isTableLoading) {
    return;
  }
  const email = globalState.userInfos ? globalState.userInfos.email : "efficience@upsell.fr";
  const body = {
    email,
    period: action.period
  };
  try {
    yield put(setTableLoading(true));

    yield call(statsApiUpsellProvider.post, "/cmdreassort", body);
    yield put(setTableLoading(false));
  } catch (e) {
    yield put(setTableLoading(false));
  }
}
function* mailAdvilGain(action) {
  const isTableLoading = yield select(getTableLoading);
  const globalState = yield select(getState);
  if (isTableLoading) {
    return;
  }
  const email = globalState.userInfos ? globalState.userInfos.email : "efficience@upsell.fr";
  const currentClientId = globalState.currentClient ? globalState.currentClient.id : null;
  const body = {
    email,
    period: action.period,
    client_id: currentClientId
  };
  try {
    yield put(setTableLoading(true));

    yield call(statsApiUpsellProvider.post, "/advilgain", body);
    yield put(setTableLoading(false));
  } catch (e) {
    yield put(setTableLoading(false));
  }
}
function* mailVisitRelevePharma(action) {
  const isTableLoading = yield select(getTableLoading);
  const globalState = yield select(getState);
  if (isTableLoading) {
    return;
  }
  const email = globalState.userInfos ? globalState.userInfos.email : "efficience@upsell.fr";
  const currentClientId = globalState.currentClient ? globalState.currentClient.id : null;
  const body = {
    email,
    period: action.period,
    client_id: currentClientId
  };
  try {
    yield put(setTableLoading(true));

    yield call(statsApiUpsellProvider.post, "/visitrelevepharma", body);
    yield put(setTableLoading(false));
  } catch (e) {
    yield put(setTableLoading(false));
  }
}

function* mailSampleParodontax() {
  const isTableLoading = yield select(getTableLoading);
  if (isTableLoading) {
    return;
  }
  try {
    yield put(setTableLoading(true));

    yield call(statsApiUpsellProvider.get, "/sampleparo");
    yield put(setTableLoading(false));
  } catch (e) {
    yield put(setTableLoading(false));
  }
}
function* mailNbVisitsPerDay(action) {
  const isTableLoading = yield select(getTableLoading);
  const globalState = yield select(getState);
  const email = globalState.userInfos ? globalState.userInfos.email : "efficience@upsell.fr";
  const currentClientId = globalState.currentClient ? globalState.currentClient.id : null;
  if (isTableLoading) {
    return;
  }
  // const stats = yield select(getStatsState); // TEMP : to limit to 4 months

  const body = {
    email,
    client_id: currentClientId,
    period: action.period
  };

  try {
    yield put(setTableLoading(true));
    yield call(statsApiUpsellProvider.post, "/nbvisitsperday", body);
    yield put(setTableLoading(false));
  } catch (e) {
    yield put(setTableLoading(false));
  }
}

function* mailNbVisitsPerDayLucca(action) {
  console.log("saga mailNbVisitsPerDayLucca");
  const isTableLoading = yield select(getTableLoading);
  const globalState = yield select(getState);
  const email = globalState.userInfos ? globalState.userInfos.email : "efficience@upsell.fr";
  const currentClientId = globalState.currentClient ? globalState.currentClient.id : null;
  if (isTableLoading) {
    return;
  }
  // const stats = yield select(getStatsState); // TEMP : to limit to 4 months

  const body = {
    email,
    client_id: currentClientId,
    period: action.period
  };

  try {
    yield put(setTableLoading(true));
    yield call(statsApiUpsellProvider.post, "/nbvisitsperdaylucca", body);
    yield put(setTableLoading(false));
  } catch (e) {
    yield put(setTableLoading(false));
  }
}
/**
 * fetch and export data
 * @return {IterableIterator<ForkEffect | *>}
 */
function* watchExportData() {
  yield takeEvery(STATS_EXPORT_DATA, exportData);
}
/**
 * fetch and export raw data
 * @return {IterableIterator<ForkEffect | *>}
 */
function* watchExportRawData() {
  yield takeEvery(STATS_EXPORT_RAW, exportRawData);
}

function* watchMailCommandReassort() {
  yield takeEvery(STATS_MAIL_COMMANDE_REASSORT, mailCommandeReassort);
}

function* watchMailSampleParodontax() {
  yield takeEvery(STATS_MAIL_SAMPLE_PARODONTAX, mailSampleParodontax);
}

function* watchMailAdvilGain() {
  yield takeEvery(STATS_MAIL_ADVIL_GAIN, mailAdvilGain);
}

function* watchMailVisitRelevePharma() {
  yield takeEvery(STATS_MAIL_VISIT_RELEVE_PHARMA, mailVisitRelevePharma);
}
function* watchMailNbVisitsPerDay() {
  yield takeEvery(STATS_MAIL_NB_VISITS_PER_DAY, mailNbVisitsPerDay);
}

function* watchMailNbVisitsPerDayLucca() {
  console.log("watchMailNbVisitsPerDayLucca");
  yield takeEvery(STATS_MAIL_NB_VISITS_PER_DAY_LUCCA, mailNbVisitsPerDayLucca);
}
/**
 * Export and download data
 * @return {Generator<SelectEffect|*, void, ?>}
 */
function* exportGainData() {
  const isTableLoading = yield select(getTableLoading);
  if (isTableLoading) {
    return;
  }

  yield put(setTableLoading(true));
  const stats = yield select(getStatsState);
  const summaryKpi = yield select(getSummaryKpiSelected);
  const kpiSelected = stats.kpiSelected;
  const currentFilters = yield select(getCurrentFiltersUnSplitted, null, true);
  const formattedFilters = StatUtils.formatFilters(currentFilters);
  const summaryDimensions = yield select(getSummaryDimensions);
  const summaryKpiId = yield select(getSummaryKpiId);
  const body = {
    filters: formattedFilters,
    period: stats.period,
    period1: stats.periodToCompare,
    summaryKpiId,
    dimensions: summaryDimensions
  };
  try {
    const data = yield call(getExportGainFile, kpiSelected, body);
    FileSaver.saveAs(
      new Blob([data], { type: "application/vnd.ms-excel" }),
      `${summaryKpi.long_label || "Données"}_GAIN.csv`
    );
    yield put(setTableLoading(false));
  } catch (e) {
    yield put(setTableLoading(false));
  }
}

/**
 * fetch and export gain data
 * @return {IterableIterator<ForkEffect | *>}
 */
function* watchExportGainData() {
  yield takeEvery(STATS_EXPORT_GAIN_DATA, exportGainData);
}

/**
 * Export and download data
 * @return {Generator<SelectEffect|*, void, ?>}
 */
function* changeDimensions({ data }) {
  yield put(setSummaryDimensions(data));
  yield put(fetchSummaryData());
}

function* watchChangeDimensions() {
  yield takeEvery(STATS_CHANGE_SUMMARY_DIMENSIONS, changeDimensions);
}

/**
 * Change summary kpi
 * @param kpi
 * @return {Generator<*, void, ?>}
 */
function* changeKpi({ kpi }) {
  yield put(setSummaryKpi(kpi));
  yield put(fetchSummaryData());
}

function* watchChangeKpi() {
  yield takeEvery(STATS_CHANGE_SUMMARY_KPI, changeKpi);
}

export default function* root() {
  yield all([fork(watchExportData)]);
  yield all([fork(watchExportRawData)]);
  yield all([fork(watchExportGainData)]);
  yield all([fork(watchChangeDimensions)]);
  yield all([fork(watchChangeKpi)]);
  yield all([fork(watchMailCommandReassort)]);
  yield all([fork(watchMailSampleParodontax)]);
  yield all([fork(watchMailNbVisitsPerDay)]);
  yield all([fork(watchMailNbVisitsPerDayLucca)]);
  yield all([fork(watchMailAdvilGain)]);
  yield all([fork(watchMailVisitRelevePharma)]);
}



// WEBPACK FOOTER //
// src/modules/stats/sagas/summary.js